import { useEffect, useState } from "react";
import {
  ArchetypeDiscovery,
  DimensionOutroSummary,
  ElementDiscovery,
  PatternDiscovery,
} from "@dimensional-engineering/dimensional-models";

import styles from "./SignatureDiscoveredElements.module.css";

import Archetype from "@/components/traits/arcehtype/Archetypes/Archetype";
import { LightContinueButton } from "@/components/shared/buttons/ContinueButton/ContinueButton";
import Element from "@/components/traits/element/Element/Element";
import Pattern from "@/components/traits/pattern/Pattern/Pattern";
import jsonElements from "../../../../../assets/traitInfo/elements.json";
import jsonPatterns from "../../../../../assets/traitInfo/patterns.json";
import {
  getUserPatternScore,
  getUserScaleScore,
} from "@/components/traits/utils";
import { useModalContext } from "@/context/ModalContext";
import ElementModal from "@/components/traits/TraitsModals/Element/ElementModal";
import PatternModal from "@/components/traits/TraitsModals/Pattern/PatternModal";
import ArchetypeModal from "@/components/traits/TraitsModals/Archetype/ArchetypeModal";
import { useStaggeredOpacity } from "@/helpers/useStaggeredOpacity";
import ThemedButton from "@/components/shared/ThemedButton/ThemedButton";

type Props = {
  outro: DimensionOutroSummary | undefined;
  onFinish: () => void;
};

export default function SignatureDiscoveredElements(props: Props) {
  const [count, setCount] = useState<number>(0);
  const [elementSignatureSlugs, setElementSignatureSlugs] = useState<string[]>(
    []
  );
  const [patternsSignatureSlugs, setPatternsSignatureSlugs] = useState<
    string[]
  >([]);

  const { setModalComponent } = useModalContext();

  const opacity = useStaggeredOpacity(5);

  useEffect(() => {
    let sigCount = 0;
    props.outro?.elementDiscoveries.forEach((el) => {
      const elScore = getUserScaleScore(
        el.scaleScore?.percentScore,
        el.scaleSlug,
        el.scaleScore?.dimensionSlugSource
      );
      if (elScore && elScore >= 0.85) {
        sigCount += 1;
        setElementSignatureSlugs((current) => [...current, el.scaleSlug]);
      }
    });

    props.outro?.archetypeDiscoveries.forEach((arch) => {
      sigCount += 1;
    });

    props.outro?.patternDiscoveries.forEach((pat) => {
      const pattScore = getUserScaleScore(pat.percentScore, pat.patternSlug);
      if (pattScore && pattScore >= 0.85) {
        sigCount += 1;
        setPatternsSignatureSlugs((current) => [...current, pat.patternSlug]);
      }
    });

    if (sigCount === 0) {
      props.onFinish();
    } else {
      setCount(sigCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mapped = [
    props.outro?.elementDiscoveries.sort((a, b) => {
      if (a.scaleScore?.scaleName && b.scaleScore?.scaleName) {
        return a.scaleScore.scaleName?.localeCompare(b.scaleScore?.scaleName);
      } else {
        return 0;
      }
    }),
    props.outro?.archetypeDiscoveries.sort((a, b) => {
      return a.archetype.name.localeCompare(b.archetype.name);
    }),
    props.outro?.patternDiscoveries.sort((a, b) => {
      const patternA = jsonPatterns?.find((p) => p.slug === a.patternSlug);
      const patternB = jsonPatterns?.find((p) => p.slug === b.patternSlug);
      if (patternA && patternB) {
        return patternA.name.localeCompare(patternB?.name);
      }
      return 0;
    }),
  ]
    .flat()
    .map((el, index) => {
      if (el?.discoveryType === "element") {
        const element = el as ElementDiscovery;
        let name;
        jsonElements.forEach((json) => {
          if (json.slug === element.scaleSlug) {
            name = json.alias ?? json.name;
          }
        });
        return (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setModalComponent(<ElementModal slug={element.scaleSlug} />);
            }}
            style={{ cursor: "pointer" }}
            key={element.scaleSlug}
            className={
              elementSignatureSlugs.includes(element.scaleSlug)
                ? "elBodyNoMarginMobile"
                : `${styles.blackedOut} elBodyNoMarginMobile`
            }
          >
            <Element
              slug={element.scaleSlug}
              score={getUserScaleScore(
                element.scaleScore?.percentScore,
                element.scaleSlug
              )}
              maxWidth
            />
          </div>
        );
      } else if (el?.discoveryType === "pattern") {
        const pattern = el as PatternDiscovery;
        return (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setModalComponent(<PatternModal slug={pattern.patternSlug} />);
            }}
            style={{ cursor: "pointer" }}
            key={pattern.patternSlug}
            className={
              patternsSignatureSlugs.includes(pattern.patternSlug)
                ? "elBodyNoMarginMobile"
                : `${styles.blackedOut} elBodyNoMarginMobile`
            }
          >
            <Pattern
              patternSlug={pattern.patternSlug}
              score={getUserPatternScore(
                pattern.percentScore,
                pattern.patternSlug,
                pattern.isLongFormGenerated
              )}
              maxWidth
            />
          </div>
        );
      } else {
        const archetype = el as ArchetypeDiscovery;
        if (archetype) {
          return (
            <div
              onClick={(e) => {
                e.stopPropagation();
                setModalComponent(
                  <ArchetypeModal slug={archetype.archetypeSlug} />
                );
              }}
              style={{ cursor: "pointer" }}
              key={archetype.archetypeSlug}
              className="elBodyNoMarginMobile"
            >
              <Archetype
                archetype={archetype.archetype}
                score={archetype.archetypeScore}
                maxWidth
              />
            </div>
          );
        }
      }
    });

  return (
    <div className={styles.main}>
      <p
        style={{ opacity: opacity.step1 }}
        className={styles.signatureTraitsText}
      >
        STANDOUT TRAITS
      </p>
      <p style={{ opacity: opacity.step2 }} className={styles.traitCount}>
        {count}
      </p>
      <p style={{ opacity: opacity.step3 }} className={styles.traitCountText}>
        Traits you scored really high on
      </p>
      <div style={{ opacity: opacity.step4 }} className={styles.parent}>
        <div className={styles.wrapper}>{mapped}</div>
      </div>
      <div style={{ opacity: opacity.step5 }} className={styles.bottom}>
        <ThemedButton
          style={{ fontWeight: "450", width: "100%" }}
          title="Go to Dimension Summary"
          onClick={(e) => {
            e.stopPropagation();
            props.onFinish();
          }}
        />
      </div>
    </div>
  );
}
